import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Asociacion, AsociacionesService, TipoAsociacion, TipoAsociacionesService } from '../../../api';
import {
  AsociacionEstadoService, ETIQUETA_ESTADO, EstadoAsociacion, ItemLote, LOTE_MAX, PreviaLote, ResultadoLote, estadoDe
} from '../../services/asociacion-estado.service';
import { AlertService } from '../alert/alert.service';
import { SpinnerComponent } from '../spinner/spinner.component';

type FiltroEstado = 'todos' | EstadoAsociacion;

export function normalizar(texto: unknown): string {
  return String(texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/**
 * Administración de asociaciones (primera versión: estado). Listado con buscador por nombre y CIF, filtros por estado y tipo,
 * selección EXPLÍCITA (máx. 100) y acciones «Activar / Desactivar seleccionadas» en dos tiempos: vista previa con recuentos
 * exactos y confirmación reforzada (frase). La API exige `asociaciones:estado` y audita cada asociación con una operación común.
 * Diseñada para incorporar más operaciones administrativas en el futuro (sección de acciones independiente del listado).
 */
import { PageHeaderComponent } from '../ui/page-header.component';

@Component({
  selector: 'app-administracion-asociaciones',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, SpinnerComponent, PageHeaderComponent],
  templateUrl: './administracion-asociaciones.component.html'
})
export class AdministracionAsociacionesComponent implements OnInit {
  readonly LOTE_MAX = LOTE_MAX;
  readonly TAMANO_PAGINA = 25;
  readonly ETIQUETA_ESTADO = ETIQUETA_ESTADO;

  cargando = true;
  error = '';
  asociaciones: Asociacion[] = [];
  tipos: TipoAsociacion[] = [];

  // Filtros
  busqueda = '';
  filtroEstado: FiltroEstado = 'todos';
  filtroTipo: number | '' = '';
  filtradas: Asociacion[] = [];
  pagina = 1;

  // Selección explícita (ids)
  readonly seleccion = new Set<number>();

  // Operación en curso
  accion: 'activar' | 'desactivar' | null = null;
  previa: PreviaLote | null = null;
  confirmacionTexto = '';
  motivo = '';
  trabajando = false;
  resultado: ResultadoLote | null = null;

  constructor(
    private readonly asociacionesService: AsociacionesService,
    private readonly tiposService: TipoAsociacionesService,
    private readonly estadoService: AsociacionEstadoService,
    private readonly alertas: AlertService
  ) {}

  ngOnInit(): void { this.cargar(); }

  cargar(): void {
    this.cargando = true;
    this.error = '';
    this.asociacionesService.asociacionesGet().subscribe({
      next: res => {
        this.asociaciones = (res.asociaciones ?? []).slice().sort((a, b) => normalizar(a.nombre).localeCompare(normalizar(b.nombre)));
        this.cargando = false;
        this.aplicarFiltros(false);
      },
      error: (err: HttpErrorResponse) => {
        this.cargando = false;
        this.error = err.error?.status?.message || err.error?.message || 'No se han podido cargar las asociaciones.';
      }
    });
    this.tiposService.tipoasociacionesGet().subscribe({
      next: (res: any) => { this.tipos = res.tipoAsociaciones ?? res.tiposAsociaciones ?? res.tipoasociaciones ?? []; },
      error: () => { this.tipos = []; }
    });
  }

  estadoDe(a: Asociacion): EstadoAsociacion { return estadoDe(a.active as number | null | undefined); }

  tipoNombre(id: number | null | undefined): string {
    if (id === null || id === undefined) return '—';
    return this.tipos.find(t => Number(t.id) === Number(id))?.tipo ?? String(id);
  }

  // --- Filtros y paginación ------------------------------------------------------------------------------------------

  aplicarFiltros(reiniciarPagina = true): void {
    const q = normalizar(this.busqueda);
    this.filtradas = this.asociaciones.filter(a => {
      if (q && !normalizar(a.nombre).includes(q) && !normalizar(a.cif).includes(q)) return false;
      if (this.filtroEstado !== 'todos' && this.estadoDe(a) !== this.filtroEstado) return false;
      if (this.filtroTipo !== '' && Number(a['tipo_asociacion']) !== Number(this.filtroTipo)) return false;
      return true;
    });
    if (reiniciarPagina) this.pagina = 1;
    this.pagina = Math.min(this.pagina, this.totalPaginas);
  }

  get totalPaginas(): number { return Math.max(1, Math.ceil(this.filtradas.length / this.TAMANO_PAGINA)); }
  get paginaActual(): Asociacion[] {
    const inicio = (this.pagina - 1) * this.TAMANO_PAGINA;
    return this.filtradas.slice(inicio, inicio + this.TAMANO_PAGINA);
  }
  irA(p: number): void { this.pagina = Math.min(Math.max(1, p), this.totalPaginas); }

  // --- Selección explícita -------------------------------------------------------------------------------------------

  estaSeleccionada(a: Asociacion): boolean { return this.seleccion.has(Number(a.id)); }

  alternar(a: Asociacion): void {
    const id = Number(a.id);
    if (this.seleccion.has(id)) { this.seleccion.delete(id); return; }
    if (this.seleccion.size >= LOTE_MAX) {
      this.alertas.warning('Una operación admite como máximo ' + LOTE_MAX + ' asociaciones.', 4000);
      return;
    }
    this.seleccion.add(id);
    this.invalidarPrevia();
  }

  get paginaTodaSeleccionada(): boolean { return this.paginaActual.length > 0 && this.paginaActual.every(a => this.estaSeleccionada(a)); }

  /** Marca o desmarca SOLO las filas visibles de la página actual (nunca el resto del listado). */
  alternarPagina(): void {
    if (this.paginaTodaSeleccionada) {
      this.paginaActual.forEach(a => this.seleccion.delete(Number(a.id)));
    } else {
      for (const a of this.paginaActual) {
        if (this.seleccion.size >= LOTE_MAX) { this.alertas.warning('Se alcanzó el máximo de ' + LOTE_MAX + ' asociaciones por operación.', 4000); break; }
        this.seleccion.add(Number(a.id));
      }
    }
    this.invalidarPrevia();
  }

  limpiarSeleccion(): void { this.seleccion.clear(); this.cancelar(); }

  get seleccionadas(): number[] { return [...this.seleccion]; }

  // --- Operación en lote (vista previa → confirmación reforzada → ejecución) ------------------------------------------

  private invalidarPrevia(): void {
    // Cambiar la selección invalida cualquier vista previa: nunca se ejecuta algo que la persona no haya revisado.
    this.previa = null; this.accion = null; this.confirmacionTexto = ''; this.resultado = null;
  }

  iniciar(accion: 'activar' | 'desactivar'): void {
    if (!this.seleccion.size || this.trabajando) return;
    this.trabajando = true;
    this.resultado = null;
    this.estadoService.previsualizarLote(this.seleccionadas, accion === 'activar').subscribe({
      next: previa => { this.trabajando = false; this.previa = previa; this.accion = accion; this.confirmacionTexto = ''; },
      error: (err: HttpErrorResponse) => { this.trabajando = false; this.alertas.danger(this.mensaje(err, 'No se ha podido preparar la operación.'), 5000); }
    });
  }

  cancelar(): void { this.invalidarPrevia(); this.motivo = ''; }

  get confirmacionValida(): boolean {
    return !!this.previa && this.previa.resumen.cambiaran > 0 && this.confirmacionTexto.trim().toUpperCase() === this.previa.confirmacion;
  }

  ejecutar(): void {
    if (!this.previa || !this.accion || !this.confirmacionValida || this.trabajando) return;
    this.trabajando = true;
    const previa = this.previa;
    this.estadoService.ejecutarLote(this.seleccionadas, this.accion === 'activar', previa.huella, this.confirmacionTexto.trim(), this.motivo).subscribe({
      next: resultado => {
        this.trabajando = false;
        this.resultado = resultado;
        this.previa = null; this.accion = null; this.confirmacionTexto = ''; this.motivo = '';
        this.seleccion.clear();
        this.alertas.success('Operación aplicada: ' + resultado.resumen.aplicadas + ' asociación(es) modificada(s).', 5000);
        this.cargar();
      },
      error: (err: HttpErrorResponse) => {
        this.trabajando = false;
        this.alertas.danger(this.mensaje(err, 'No se ha podido aplicar la operación.'), 6000);
        // 409: la selección cambió o la operación ya se aplicó → hay que volver a revisar
        if (err.status === 409) { this.previa = null; this.accion = null; this.confirmacionTexto = ''; this.cargar(); }
      }
    });
  }

  private mensaje(err: HttpErrorResponse, porDefecto: string): string {
    return err.error?.status?.message || err.error?.message || porDefecto;
  }

  etiquetaResultado(i: ItemLote): string {
    switch (i.resultado) {
      case 'cambiara': return 'Se cambiará';
      case 'aplicada': return 'Aplicada';
      case 'sin_cambios': return 'Sin cambios';
      default: return 'Error';
    }
  }

  etiquetaEstadoNumero(valor: number | null | undefined): string { return ETIQUETA_ESTADO[estadoDe(valor)]; }
}
